const StorageTypes = require('./jsdoc-types'); // eslint-disable-line no-unused-vars
const { compose } = require('../fp');
const { createInMemoryStorage } = require('./storages/inMemoryStorage');
const createStorageLensForDedicatedStorage = require('./lenses/createStorageLensForDedicatedStorage');
const createStorageLensForSingleEntryOfStorage = require('./lenses/createStorageLensForSingleEntryOfStorage');
const storageKeyRegistry = require('./storageKeyRegistry');
const createLogicalStorage = require('./storages/logicalStorage');

/**
 * Physical storage backends.
 * @readonly
 * @enum {number}
 */
const STORAGE_BACKEND = {
  MEMORY: 1,
  LOCAL_STORAGE: 2,
  SESSION_STORAGE: 3,
};

const StorageFactory = {
  /**
   * Creates a logical key/value storage backed by a physical backend.
   *
   * The factory composes:
   * - a physical backend (memory, localStorage, sessionStorage)
   * - a storage lens (representation layer)
   * - a logical storage abstraction
   * - optional decorators (validation, TTL)
   *
   * @param {Object} options Factory options
   *
   * @param {STORAGE_BACKEND} options.backend
   *   The physical backend Storage used to store data.
   *
   * @param {string} [options.name]
   *   Required for LOCAL_STORAGE and SESSION_STORAGE.
   *   This is the physical key where the logical storage is saved.
   *
   * @param {StorageTypes.StorageDecorator[]} [options.decorators]
   *   Storage decorators to apply
   *
   * @returns {Storage}
   *   A composed logical storage instance with optional decorators.
   *
   * @throws {TypeError}
   *   If backend is unknown or required fields are missing.
   */
  createStorage({ backend, name, decorators }) {
    let physicalStorage;
    let storageLens;

    if (backend === STORAGE_BACKEND.MEMORY) {
      physicalStorage = createInMemoryStorage();
      storageLens = createStorageLensForDedicatedStorage();
    } else if (
      backend === STORAGE_BACKEND.LOCAL_STORAGE ||
      backend === STORAGE_BACKEND.SESSION_STORAGE
    ) {
      if (!name) {
        throw new TypeError('"name" argument is required for this backend');
      }

      storageKeyRegistry.registerKey(name, backend);

      physicalStorage =
        backend === STORAGE_BACKEND.LOCAL_STORAGE
          ? localStorage
          : sessionStorage;

      storageLens = createStorageLensForSingleEntryOfStorage(name);
    } else {
      throw new TypeError('Unknown storage backend');
    }

    let storageInstance = createLogicalStorage({
      innerStorage: physicalStorage,
      storageLens,
    });

    if (decorators) {
      const decorate = compose(...decorators);
      storageInstance = decorate(storageInstance);
    }

    return storageInstance;
  },
};

module.exports = {
  StorageFactory,
  STORAGE_BACKEND,
};
